import { useIntl } from "react-intl";
import { NavLink } from "react-router";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import Rome2Rio from "src/design-system/icons/Rome2Rio.svg?react";
import Rome2RioMonogram from "src/design-system/icons/Rome2RioMonogram.svg?react";
import { tw } from "src/design-system/tw";
import { DebugDropdown } from "src/domain/TopNav/DebugDropdown/DebugDropdown";
import { headerMessages as messages } from "src/domain/TopNav/TopNav.messages";
import { UserDropdown } from "src/domain/TopNav/UserDropdown/UserDropdown";
import { useFeature } from "src/feature/useFeature";
import { capitalize } from "src/utils/capitalize";
import { localeToLanguageCode } from "src/utils/conversions/languageCode";
import { useNavigateTabs } from "src/utils/hooks/useNavigateTabs";
import { useTabKind } from "src/utils/hooks/useTabKind";
import {
  useTypedLocation,
  type LocationState,
} from "src/utils/hooks/useTypedLocation";
import { getHomepageUrl } from "src/utils/url";
import { UserPreferencesButton } from "../UserPreferencesButton/UserPreferencesButton";
import { CarLink } from "./CarLink";
import { GlobalSearch } from "./GlobalSearch";

export function GlobalHeader() {
  const screenKind = useTabKind();
  const isDebug = useFeature("Debug");
  const isDatesSearchBar = useFeature("DatesSearchBar") !== "Baseline";

  return (
    <header className="sticky top-0 z-10 flex w-full flex-col items-center gap-2 bg-surface px-3 py-2 shadow-md 3xl:px-8">
      <nav className="flex w-[var(--breakpoint-3xl)] max-w-full flex-wrap items-center justify-between gap-2 2xl:gap-4">
        <Logo />
        <GlobalSearch
          screenKind={screenKind}
          className={tw("order-1 xl:order-0", isDatesSearchBar && "flex-0")}
        />
        <div className="flex justify-center lg:min-w-1/2 xl:mr-auto xl:min-w-0">
          <Tabs />
        </div>
        <div className="flex items-center gap-4">
          <UserPreferencesButton />
          <UserDropdown />
          {isDebug && <DebugDropdown />}
        </div>
      </nav>
    </header>
  );
}

function Logo() {
  const intl = useIntl();

  const languageCode = localeToLanguageCode(intl.locale);
  const homepageUrl = getHomepageUrl(languageCode);

  return (
    <a className="flex shrink" href={homepageUrl}>
      <span className="sr-only">{intl.formatMessage(messages.homepage)}</span>
      <Rome2RioMonogram
        aria-hidden="true"
        className="hidden h-8 w-8 xl:block 2xl:hidden"
      />
      <Rome2Rio aria-hidden="true" className="w-30 xl:hidden 2xl:block" />
    </a>
  );
}

function Tabs() {
  const location = useTypedLocation();
  const { tabs } = useNavigateTabs();
  const intl = useIntl();
  const screenKind = useTabKind();

  return (
    <div className="flex items-center">
      {Object.entries(tabs).map(([key, tab]) => {
        const isSelected = key === screenKind;
        const newLocationState: LocationState = {
          ...location.state,
          highlightedTab: tab.highlightedTab,
        };
        return (
          <NavLink
            key={key}
            className={tw(
              "relative h-fit cursor-pointer rounded-lg p-2 text-label-lg font-medium text-text-primary no-underline transition-all duration-100 hover:bg-border-secondary hover:text-text-primary-hover 2xl:px-3",
              isSelected && "text-text-primary-active cursor-default",
            )}
            to={{
              pathname: location.pathname,
              search: location.search,
              hash: tab.hash,
            }}
            state={newLocationState}
            onClick={() => {
              sendAnalyticsInteractionEvent({
                category: "TopNav",
                action: `Click:${capitalize(key)}`,
              });
            }}
            prefetch="intent"
          >
            {intl.formatMessage(tab.message)}
          </NavLink>
        );
      })}
      <CarLink
        eventCategory="TopNav"
        className="relative h-fit cursor-pointer rounded-lg px-3 py-2 text-label-lg font-medium text-text-primary no-underline transition-all duration-100 hover:bg-border-secondary hover:text-text-primary-active"
      />
    </div>
  );
}

export function getHeaderHeightPadded(): number {
  const computed = getComputedStyle(document.documentElement);
  const height = computed.getPropertyValue("--spacing-header-height-padded");
  return parseFloat(height);
}
